import { useTranslation } from "@sagot/i18n";
import { EmptyState, ErrorState, Icon, Segmented, TableSkeletonRows, cssVars, type IconName } from "@sagot/ui";
import { useState } from "react";
import { useFormatters } from "../shared";
import { useUsageBreakdown } from "./use-billing";
import { TableScroll } from "./table-scroll";

type By = "agent" | "channel" | "feature";
const COLUMNS = 4;
const CHANNEL_ICONS: Readonly<Record<string, IconName>> = { widget: "globe", messenger: "message-circle", webapp: "app-window", api: "code", mobile: "smartphone", email: "mail", playground: "flask" };

function ShareBar({ pct, label }: { pct: number; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 rounded-pill bg-sunken" aria-hidden="true">
        <div className="h-2 w-[var(--geo-width)] rounded-pill bg-brand-500" style={cssVars({ "--geo-width": `${String(Math.min(pct, 100))}%` })} />
      </div>
      <span className="w-10 text-right text-xs tabular-nums text-fg-muted">
        {Math.round(pct)}%<span className="sr-only"> {label}</span>
      </span>
    </div>
  );
}

/** Credits used this cycle by agent or by channel. */
export function UsageBreakdownCard() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [by, setBy] = useState<By>("agent");
  const breakdown = useUsageBreakdown(by);
  const rows = breakdown.data?.rows ?? [];
  const totals = rows.reduce((sum, row) => ({ answers: sum.answers + row.answers, credits: sum.credits + row.credits }), { answers: 0, credits: 0 });
  const label = (key: string, fallback: string) => {
    if (by === "channel") return t(`billing.channels.${key}`, { defaultValue: fallback });
    return by === "feature" ? t(`billing.breakdown.features.${key}`, { defaultValue: fallback }) : fallback;
  };
  return (
    <section className="card overflow-hidden" aria-labelledby="breakdown-title">
      <div className="card-header">
        <h2 id="breakdown-title" className="card-title">
          {t("billing.breakdown.title")}
        </h2>
        <Segmented aria-label={t("billing.breakdown.title")} value={by} onValueChange={setBy} options={[{ value: "agent", label: t("billing.breakdown.byAgent") }, { value: "channel", label: t("billing.breakdown.byChannel") }, { value: "feature", label: t("billing.breakdown.byFeature") }]} />
      </div>
      {breakdown.isError ? (
        <ErrorState headingLevel={3} title={t("billing.breakdown.loadError")} onRetry={() => void breakdown.refetch()} retryLabel={t("common.retry")} />
      ) : (
        <TableScroll label={t("billing.breakdown.title")}>
          <table className="table">
            <thead>
              <tr>
                <th>{t(`billing.breakdown.${by}`)}</th>
                <th className="text-right">{t("billing.breakdown.answers")}</th>
                <th className="text-right">{t("billing.breakdown.credits")}</th>
                <th className="w-64">{t("billing.breakdown.share")}</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.isPending ? (
                <TableSkeletonRows rows={3} columns={COLUMNS} />
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS}>
                    <EmptyState icon="bar-chart" headingLevel={3} title={t("billing.breakdown.emptyTitle")} description={t("billing.breakdown.emptyBody")} />
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <span className="flex items-center gap-2 font-semibold">
                          {by === "channel" ? <Icon name={CHANNEL_ICONS[row.key] ?? "globe"} className="size-4 text-fg-subtle" /> : null}
                          {label(row.key, row.label)}
                        </span>
                      </td>
                      <td className="text-right tabular-nums">{fmt.number(row.answers)}</td>
                      <td className="text-right tabular-nums">{fmt.number(row.credits)}</td>
                      <td>
                        <ShareBar pct={row.share_pct} label={t("billing.breakdown.share")} />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-surface-2">
                    <td className="font-bold">{t("billing.breakdown.total")}</td>
                    <td className="text-right font-bold tabular-nums">{fmt.number(totals.answers)}</td>
                    <td className="text-right font-bold tabular-nums">{fmt.number(totals.credits)}</td>
                    <td />
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </TableScroll>
      )}
    </section>
  );
}
